import { useCallback, useEffect, useState } from 'react';

import {
  PLATFORM_LABELS,
  type HeldEarning,
  type HeldEarningsResponse,
  type Platform,
  type ReviewItem,
  type ReviewQueueResponse,
} from '@nexus/shared';

import { ApiError, apiGet, apiSend } from '../../lib/api';
import { CheckList } from '../../components/CheckList';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmButton,
  EmptyState,
  ExternalLink,
  Fact,
  Facts,
  LoadingPage,
  Money,
  Num,
  PageHeader,
  Stat,
  StatGrid,
  TextField,
  When,
  checkName,
  statusTone,
} from '../../ui';

/**
 * The content review queue.
 *
 * The automatic checks have already decided ownership and timing, so the only
 * question left on this screen is the one a person has to answer: does this
 * clip follow the campaign's rules. Everything needed to answer it is on the
 * card — the post, the rules as this creator accepted them, what they have
 * submitted before, and every check with its note — because a queue that makes
 * the operator open three tabs is a queue where the rules stop being read.
 *
 * Held earnings sit ABOVE the content queue, because they are money and the
 * content queue is not.
 */
export function AdminReview(): React.JSX.Element {
  const [items, setItems] = useState<ReviewItem[] | null>(null);
  const [held, setHeld] = useState<HeldEarning[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const body = await apiGet<ReviewQueueResponse>('/admin/review');
      setItems(body.items);
    } catch {
      setError('Could not load the review queue.');
    }
    try {
      /* A second call, and a failure here does NOT blank the content queue:
         the two halves of this page are independent jobs and one being down
         must not stop the other being worked. */
      const money = await apiGet<HeldEarningsResponse>('/admin/earnings/held');
      setHeld(money.earnings);
    } catch {
      /* Left as it was; the section simply does not render. */
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <>
        <PageHeader eyebrow="Operator" title="Review queue" />
        <Alert tone="critical" title="Could not load the review queue.">
          <span>Nothing has been approved or rejected. Reload and try again.</span>
        </Alert>
      </>
    );
  }

  if (items === null) return <LoadingPage eyebrow="Operator" title="Review queue" stats />;

  const heldTotal = held.reduce((sum, e) => sum + e.amountCents, 0);

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title="Review queue"
        lead="Ownership and timing are already settled. The only question left is whether the clip follows the campaign's rules."
      />

      <StatGrid>
        <Stat
          label="Waiting for review"
          value={<Num value={items.length} />}
          note={items.length === 0 ? 'Nobody is waiting on you.' : 'Oldest first.'}
          emphasis={items.length > 0}
        />
        <Stat
          label="Held earnings"
          value={<Num value={held.length} />}
          note="Stopped by a fraud rule."
        />
        <Stat label="Money held" value={<Money cents={heldTotal} />} />
      </StatGrid>

      {held.length > 0 && (
        <Card
          title="Held earnings"
          sub="Releasing pays it in the next batch; voiding reverses it and returns the money to the campaign's budget. Both need a reason."
          aside={<Badge tone="critical">{held.length} to decide</Badge>}
        >
          <p className="muted">
            A held earning has already been calculated and posted to the ledger. Nothing moves
            until you decide.
          </p>
        </Card>
      )}

      {held.map((earning) => (
        <HeldEarningCard key={earning.id} earning={earning} onDecided={load} />
      ))}

      {items.length === 0 ? (
        <Card>
          <EmptyState headingLevel={2} title="Nothing is waiting.">
            Approving a submission marks it verified and the stats worker starts reading its
            views. Anything waiting on you will appear here and on Telegram.
          </EmptyState>
        </Card>
      ) : (
        items.map((item) => <ReviewCard key={item.submission.id} item={item} onDecided={load} />)
      )}
    </>
  );
}

/**
 * One held earning, with every rule that did not pass.
 *
 * The rule that held it is named first and in its own words — the same
 * sentence the creator is shown — and the others are listed beneath, because
 * an operator deciding whether a curve is bought wants to know that the
 * engagement ratio was fine at the same time.
 */
function HeldEarningCard({
  earning,
  onDecided,
}: {
  earning: HeldEarning;
  onDecided: () => Promise<void>;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(action: 'release' | 'void'): Promise<void> {
    if (reason.trim().length === 0) {
      setError(
        action === 'release'
          ? 'A release needs a reason: you are overriding a rule that fired on evidence.'
          : 'A void needs a reason: the creator is shown it word for word.',
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiSend('POST', `/admin/earnings/${earning.id}/${action}`, { reason: reason.trim() });
      await onDecided();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the API.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      label={`Held earning ${earning.id}`}
      eyebrow={earning.campaignName}
      title={earning.creatorDisplayName ?? earning.creatorEmail}
      aside={<Badge tone="critical">{checkName(earning.heldCheck)}</Badge>}
    >
      <Alert tone="critical">{earning.heldReason}</Alert>

      <StatGrid>
        <Stat label="Amount" value={<Money cents={earning.amountCents} />} emphasis />
        <Stat label="Verified views" value={<Num value={earning.verifiedViews} />} />
        <Stat
          label="Held"
          value={<When value={earning.heldAt} withTime />}
        />
      </StatGrid>

      <Facts>
        <Fact label="Creator">{earning.creatorEmail}</Fact>
        <Fact label="Post">
          {earning.postUrl ? (
            <ExternalLink href={earning.postUrl}>open the clip</ExternalLink>
          ) : (
            <span className="muted">—</span>
          )}
        </Fact>
      </Facts>

      {earning.riskFlags.length > 0 && (
        <div>
          <h3>What every rule said</h3>
          <ol className="checklist">
            {earning.riskFlags.map((flag, index) => (
              <li
                key={`${flag.check}-${index}`}
                data-state={flag.result === 'PASS' ? 'pass' : 'fail'}
              >
                <span className="mark" aria-hidden="true">
                  {flag.result === 'PASS' ? '✓' : '✕'}
                </span>
                <span>
                  <span className="check-label">{checkName(flag.check)}</span>
                  <div className="check-note">{flag.note}</div>
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <TextField
        label="Reason"
        id={`earning-reason-${earning.id}`}
        textarea
        rows={2}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        hint="Required for both. A release overrides a rule that fired on evidence; a void is shown to the creator word for word."
        {...(error ? { 'aria-invalid': true } : {})}
      />

      {error && <Alert tone="critical">{error}</Alert>}

      <div className="actions">
        <Button variant="primary" disabled={busy} onClick={() => void decide('release')}>
          Release
        </Button>
        <ConfirmButton
          label="Void"
          title="Void this earning?"
          question="The earning is reversed in the ledger and the money returns to the campaign's budget. The creator is shown your reason word for word. This cannot be undone."
          confirmLabel="Void it"
          disabled={busy}
          onConfirm={() => void decide('void')}
        />
      </div>
    </Card>
  );
}

function ReviewCard({
  item,
  onDecided,
}: {
  item: ReviewItem;
  onDecided: () => Promise<void>;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { submission, creator, rules } = item;

  async function decide(action: 'approve' | 'reject' | 'hold'): Promise<void> {
    /* The reason is required here as well as in the Worker, so the operator is
       told before the round trip rather than after it. The database is what
       actually refuses a rejection with no reason. */
    if (action !== 'approve' && reason.trim().length === 0) {
      setError(`A ${action} needs a reason — the creator is shown it word for word.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiSend('POST', `/admin/submissions/${submission.id}/${action}`, {
        ...(reason.trim().length > 0 ? { reason: reason.trim() } : {}),
      });
      await onDecided();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the API.');
    } finally {
      setBusy(false);
    }
  }

  const platform = PLATFORM_LABELS[submission.platform as Platform] ?? submission.platform;

  return (
    <Card
      label={`Review ${submission.postUrl}`}
      eyebrow={
        <>
          {submission.campaignName} <span aria-hidden="true">·</span> {platform}{' '}
          {submission.accountHandle ?? ''}
        </>
      }
      title={creator.displayName ?? creator.email}
      aside={
        <Badge tone={statusTone(submission.status, 'submission')}>{submission.statusLabel}</Badge>
      }
    >
      <p>
        <ExternalLink href={submission.postUrl} />
      </p>

      <Facts>
        <Fact label="Submitted">
          <When value={submission.submittedAt} withTime />
        </Fact>
        <Fact label="Posted">
          <When value={submission.postPublishedAt} withTime />
        </Fact>
        <Fact label="Terms accepted">{rules.acceptedTermsVersion ?? '—'}</Fact>
        <Fact label="History">
          {creator.history.submissions} submitted · {creator.history.approved} approved ·{' '}
          {creator.history.rejected} rejected · {creator.history.held} held
        </Fact>
      </Facts>

      {submission.holdReason && <Alert tone="waiting">{submission.holdReason}</Alert>}

      <div className="split">
        <div className="stack tight">
          <h3>The rules this is judged against</h3>
          {rules.contentRules && <pre className="prose-block">{rules.contentRules}</pre>}
          <p className="muted">Caption: {rules.sentence}</p>
        </div>
        <div className="stack tight">
          <h3>Automatic checks</h3>
          <CheckList checks={submission.checks} stoppedAt={null} />
        </div>
      </div>

      <TextField
        label="Reason"
        id={`reason-${submission.id}`}
        textarea
        maxLength={2000}
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        hint="Required to reject or hold, optional on an approval. The creator reads it word for word."
        {...(error ? { 'aria-invalid': true } : {})}
      />

      {error && <Alert tone="critical">{error}</Alert>}

      <div className="actions">
        <Button variant="primary" disabled={busy} onClick={() => void decide('approve')}>
          Approve
        </Button>
        <Button variant="danger" disabled={busy} onClick={() => void decide('reject')}>
          Reject
        </Button>
        <Button disabled={busy} onClick={() => void decide('hold')}>
          Hold
        </Button>
      </div>
    </Card>
  );
}
